import { type InputCapture } from '@canvas/input';
import type { PointerDownCapture, PointerMoveCapture } from '@canvas/input/types/pointer';

import { type SelectionsMachine } from '../../../machine/selection-machine';
import { type ClickTarget } from '../utils/get-click-target';

import { dispatchInitialClick } from './dispatch-initial-click';

export interface MouseState {
  onPointerDown(event: PointerDownCapture): MouseState;
  onPointerMove(event: PointerMoveCapture): MouseState;
  onPointerUp(): MouseState;
}

export function createMouseStateMachine(
  machine: SelectionsMachine,
  inputCapture: InputCapture,
  getClickTarget: (x: number, y: number) => ClickTarget
): MouseState {
  const idleState: MouseState = {
    // There's no way to get a pointerup or pointermove
    // event in the idle state, so we just fill these with idleState
    onPointerUp: () => idleState,
    onPointerMove: () => idleState,
    onPointerDown: (event) => {
      const { x, y } = event.properties;
      const target = getClickTarget(x, y);

      if (target.type === 'none') {
        return idleState;
      }

      const hasShift = inputCapture.hasKeyModifierPressed('Shift');

      // since we're in pointerdown, we still want to dispatch
      // what we've clicked on
      dispatchInitialClick(machine, target, hasShift);

      // if we're in a pointerdown state and we're not shift
      // clicking, we can transition to a dragging state
      if (target.type === 'cell' && !hasShift) {
        return draggingState;
      }

      return idleState;
    },
  };

  const draggingState: MouseState = {
    // If the pointer is down and we continue to get
    // pointerdown events, we ignore them
    onPointerDown: () => draggingState,

    onPointerMove: (event) => {
      const { x, y } = event.properties;
      const target = getClickTarget(x, y);

      // Only extend if we are over a valid cell
      if (target.type === 'cell') {
        machine.dispatch({
          type: 'extendToCell',
          cell: target.position,
          source: 'mouse',
        });
      }

      // Stay Dragging
      return draggingState;
    },

    // if the pointerup event happens while dragging
    // a user has stopped dragging and we transition
    // back to idleState.
    onPointerUp: () => {
      return idleState;
    },
  };

  return idleState;
}
